/**
 * ImageTabContent Component
 *
 * Renders the content for the active top-level Image tab. There are three:
 *   - generate    → Text to Image
 *   - image-edit  → unified, model-first editing (img2img, face swap, inpaint,
 *                   outpaint, upscale, enhance, remove BG, describe, AI editors)
 *   - posts       → Posts & Carousels
 */

import type React from 'react';
import { memo } from 'react';
import { Palette } from 'lucide-react';
import { ImageConfigForm } from '../../components/image';
import { ImageEditTab } from '../../components/image/tabs/ImageEditTab';
import { PostsTab } from '../../components/image/tabs/PostsTab';
import { useTranslation } from '../../hooks/useTranslation';
import type { ImageTab } from './types';
import type { ImageGenerationConfig } from '../../types';
import type { CostGate } from '../../utils/costGate';

interface ImageTabContentProps {
  activeTab: ImageTab;
  config: ImageGenerationConfig;
  onConfigChange: React.Dispatch<React.SetStateAction<ImageGenerationConfig>>;
  onSubmit: () => void;
  isLoading: boolean;
  error: string | null;
  onUsePrompt: (prompt: string) => void;
  /** Forwarded to ImageConfigForm — see its prop doc for why the page needs it. */
  onEstimateChange?: (estimate: CostGate) => void;
}

export const ImageTabContent = memo(function ImageTabContent({
  activeTab,
  config,
  onConfigChange,
  onSubmit,
  isLoading,
  error,
  onUsePrompt,
  onEstimateChange,
}: ImageTabContentProps) {
  const { t } = useTranslation();

  return (
    <div className="card p-6" data-testid="image-config-container">
      {activeTab === 'generate' && (
        <>
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4 flex items-center space-x-2">
            <Palette className="h-5 w-5" />
            <span>
              {t('image.sections.configuration', { defaultValue: 'Image Configuration' })}
            </span>
          </h3>
          <ImageConfigForm
            config={config}
            onConfigChange={onConfigChange}
            onSubmit={onSubmit}
            isLoading={isLoading}
            error={error}
            onEstimateChange={onEstimateChange}
          />
        </>
      )}

      {activeTab === 'image-edit' && <ImageEditTab onUsePrompt={onUsePrompt} />}

      {activeTab === 'posts' && <PostsTab />}
    </div>
  );
});
